import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { isEmailAllowed, resetAllowlistCache } from "./allowlist";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Browser-level redirect that avoids back/forward loop
  const forceToLogin = (reason = "revoked") => {
    if (window.location.pathname === "/login") return; // already there; reloading could loop
    const next = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    window.location.replace(`/login?reason=${reason}&next=${next}`);
  };

  useEffect(() => {
    let alive = true;

    // --- initial session load ---
    // Always clear `loading` in finally. Blocking this on an allowlist rpc used to
    // leave external users stuck forever on the loading screen (see the deadlock
    // note on onAuthStateChange below). The allowlist is enforced in the effect
    // further down, which runs off the auth lock.
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (alive) setSession(data.session || null);
      } catch {
        if (alive) setSession(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    // --- session changes ---
    // CRITICAL: keep this callback synchronous and free of awaited Supabase calls.
    // gotrue invokes it while holding its internal navigator-lock; awaiting an rpc
    // here (e.g. the allowlist check) needs that same lock and DEADLOCKS — external
    // users would hang forever on "Checking your access…". Just mirror the session;
    // the allowlist is enforced off-lock in the effect below.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });

    return () => {
      alive = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  // Allowlist enforcement — runs OFF the gotrue lock (a normal effect, not the
  // auth callback), so the rpc can't deadlock. Fires once per session/email
  // (initial load, login) to gate access, and again on tab focus/visibility and a
  // slow interval so an admin deactivating or removing a client mid-session takes
  // effect within seconds instead of only on the client's next manual reload.
  // Internal @flipspaces users are always allowed and skip the rpc entirely.
  useEffect(() => {
    const email = session?.user?.email;
    if (!email) return;
    if (email.toLowerCase().endsWith("@flipspaces.com")) return; // always allowed

    let alive = true;
    const enforce = async () => {
      // Query directly rather than isEmailAllowed() (which fails closed) so a
      // transient network error can't spuriously sign a valid user out — only a
      // definitive "not allowed" answer from the DB does.
      const { data, error } = await supabase.rpc("is_email_allowed", {
        check_email: email.toLowerCase(),
      });
      if (!alive || error) return;
      if (data !== true) {
        resetAllowlistCache();
        // signOut() keeps the stored session when its server call fails, and with
        // several tabs open another tab can write it back. Either way /login would
        // find a session and bounce straight back here: a redirect loop (seen in
        // test 2.13 with 4 tabs). On a failed signOut, drop the stored session
        // ourselves; LoginPage also refuses to auto-redirect on reason=revoked.
        const { error: signOutError } = await supabase.auth.signOut();
        if (signOutError) {
          try {
            window.localStorage.removeItem(supabase.auth.storageKey);
          } catch {}
        }
        forceToLogin("revoked");
      }
    };

    enforce(); // gate this session on mount / whenever the signed-in email changes

    const onFocus = () => enforce();
    const onVisible = () => document.visibilityState === "visible" && enforce();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const iv = setInterval(enforce, 120000); // every 2 min for a tab left open

    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(iv);
    };
  }, [session?.user?.email]);

  const value = useMemo(() => {
    return {
      session,
      user: session?.user || null,
      loading,

      // A) Send OTP only if allowed
      async sendOtp(email) {
        resetAllowlistCache();
        const ok = await isEmailAllowed(email);

        if (!ok) return { ok: false, error: "Not allowed to access this app." };

        const { error } = await supabase.auth.signInWithOtp({ email });
        return error ? { ok: false, error: error.message } : { ok: true };
      },

      // B) Verify OTP only if allowed
      async verifyOtp(email, token) {
        resetAllowlistCache();
        const ok = await isEmailAllowed(email);

        if (!ok) return { ok: false, error: "Not allowed to access this app." };

        const { data, error } = await supabase.auth.verifyOtp({
          email,
          token,
          type: "email",
        });

        return error
          ? { ok: false, error: error.message }
          : { ok: true, session: data.session };
      },

      // ✅ Logout should also hard redirect to avoid history weirdness
      async signOut() {
        await supabase.auth.signOut();
        forceToLogin("logout");
      },
    };
  }, [session, loading]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
